export interface ImageValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface ImageValidationOptions {
  maxSizeBytes: number;
  allowedFormats: string[];
  minWidth: number;
  minHeight: number;
  aspectRatio?: number; // If provided, enforces specific aspect ratio (width/height)
}

export const COUPON_IMAGE_VALIDATION_OPTIONS: ImageValidationOptions = {
  maxSizeBytes: 10 * 1024 * 1024, // 10MB
  allowedFormats: ['image/jpeg', 'image/jpg', 'image/png'],
  minWidth: 650,
  minHeight: 650,
  aspectRatio: 1, // 1:1 ratio
};

export const validateImage = async (
  file: File, 
  options: ImageValidationOptions = COUPON_IMAGE_VALIDATION_OPTIONS,
  t?: (key: string, params?: any) => string
): Promise<ImageValidationResult> => {
  const errors: string[] = [];

  // Validate file size
  validateFileSize(file, options, errors, t);
  
  // Validate file format
  validateFileFormat(file, options, errors, t);

  // Validate image dimensions
  await validateImageDimensions(file, options, errors, t);

  return {
    isValid: errors.length === 0,
    errors
  };
};

const validateFileSize = (
  file: File,
  options: ImageValidationOptions,
  errors: string[],
  t?: (key: string, params?: any) => string
): void => {
  if (file.size <= options.maxSizeBytes) return;

  const maxSizeMB = options.maxSizeBytes / (1024 * 1024);
  const errorMessage = t 
    ? t('coupon.form.imageUpload.validation.sizeExceeded', { maxSize: maxSizeMB })
    : `Image size must be less than ${maxSizeMB}MB`;
  
  errors.push(errorMessage);
};

const validateFileFormat = (
  file: File,
  options: ImageValidationOptions,
  errors: string[],
  t?: (key: string, params?: any) => string
): void => {
  if (options.allowedFormats.includes(file.type)) return;

  const allowedExtensions = options.allowedFormats
    .map(format => format.split('/')[1].toUpperCase())
    .join(', ');
  
  const errorMessage = t
    ? t('coupon.form.imageUpload.validation.invalidFormat', { formats: allowedExtensions })
    : `Image format must be ${allowedExtensions}`;
  
  errors.push(errorMessage);
};

const validateImageDimensions = async (
  file: File,
  options: ImageValidationOptions,
  errors: string[],
  t?: (key: string, params?: any) => string
): Promise<void> => {
  try {
    const dimensions = await getImageDimensions(file);
    
    validateDimensionConstraints(dimensions, options, errors, t);
    validateAspectRatio(dimensions, options, errors, t);
  } catch (error) {
    const errorMessage = t
      ? t('coupon.form.imageUpload.validation.invalidImage')
      : 'Could not read image dimensions. Please ensure the file is a valid image.';
    
    errors.push(errorMessage);
  }
};

const validateDimensionConstraints = (
  dimensions: { width: number; height: number },
  options: ImageValidationOptions,
  errors: string[],
  t?: (key: string, params?: any) => string
): void => {
  if (dimensions.width < options.minWidth) {
    const errorMessage = t
      ? t('coupon.form.imageUpload.validation.widthTooSmall', { minWidth: options.minWidth })
      : `Image width must be at least ${options.minWidth}px`;
    
    errors.push(errorMessage);
  }
  
  if (dimensions.height < options.minHeight) {
    const errorMessage = t
      ? t('coupon.form.imageUpload.validation.heightTooSmall', { minHeight: options.minHeight })
      : `Image height must be at least ${options.minHeight}px`;
    
    errors.push(errorMessage);
  }
};

const validateAspectRatio = (
  dimensions: { width: number; height: number },
  options: ImageValidationOptions,
  errors: string[],
  t?: (key: string, params?: any) => string
): void => {
  if (!options.aspectRatio) return;

  const actualRatio = dimensions.width / dimensions.height;
  const tolerance = 0.1; // Allow 10% tolerance for aspect ratio
  
  if (Math.abs(actualRatio - options.aspectRatio) <= tolerance) return;

  let errorMessage: string;
  if (t) {
    errorMessage = t('coupon.form.imageUpload.validation.invalidAspectRatio');
  } else if (options.aspectRatio === 1) {
    errorMessage = 'Image must have a 1:1 aspect ratio (square)';
  } else {
    errorMessage = `Image must have a ${options.aspectRatio}:1 aspect ratio`;
  }
  
  errors.push(errorMessage);
};

const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    
    img.src = url;
  });
};

export const formatImageValidationErrors = (errors: string[]): string => {
  if (errors.length === 0) return '';
  if (errors.length === 1) return errors[0];
  
  return errors.map((error, index) => `${index + 1}. ${error}`).join('\n');
};